import type { ComponentProps } from "react";
import { sanitizeUrl } from "@helpers";
import type { IFoodProfileSummaryData } from "@models/NewFoodProfileSummary";
import type Chart from "@pages/Food/components/SummaryChart/SummaryChart";

export const pctValueFormatter = (v: number) => {
    try {
        return `${v.toFixed(1)}%`;
    } catch (err) {
        debugger
        return 'N/A';
    }
};
export const pctValueSignFormatter = (v: number) => {
    try {
        return `${v > 0 ? '+' : ''}${v.toFixed(1)}%`;
    } catch (err) {
        return 'N/A';
    }
};

export type PenetrationChartData = Pick<ComponentProps<typeof Chart>, 'pastDataAll' | 'currentDataAll' | 'futureDataAll'>;

type PenetrationPoint = IFoodProfileSummaryData['slices'][number]['points']['quarters'][number];

/**
 * The year in a quarter label ("2026 Q4" → "2026"): the last 4 digits of the last run of 4+ digits,
 * or the label itself when it has none.
 */
const getQuarterYear = (label: string) => label.match(/\d{4,}/g)?.at(-1)?.slice(-4) ?? label;

/**
 * Maps the penetration slices (one per restaurant segment) to the series the summary chart draws.
 * `null` (no chart) when there are no slices, or when a slice has no quarterly points: every series needs its
 * current point (the latest quarter), and `getSlice` defaults missing data to empty arrays.
 */
export const buildPenetrationChartData = (slices: IFoodProfileSummaryData['slices']): PenetrationChartData | null => {
    if (slices.length === 0 || slices.some(slice => slice.points.quarters.length === 0)) {
        return null;
    }
    return {
        pastDataAll: slices.map(slice => [
            ...slice.points.years.map(point => ({
                value: point.value,
                label: point.label,
            })),

        ]),
        currentDataAll: slices.map(slice => {
            const { quarters, predictedQuarters } = slice.points;
            // Never undefined: slices without quarters return `null` above.
            const lastQuarter = quarters.at(-1) as PenetrationPoint;
            const year = getQuarterYear(lastQuarter.label);

            return {
                label: year,
                value: lastQuarter.value,
                tooltipLabel: year,
                tooltipValues: [
                    ...quarters.map(point => point.value),
                    ...predictedQuarters.map(point => point.value),
                ],
                tooltipValuesSeparatorIndex: predictedQuarters.length > 0 ? (quarters.length - 1) : undefined,
            };
        }),
        futureDataAll: slices.map(slice => [
            ...slice.points.predictedYears.map(point => ({
                value: point.value,
                label: point.label,
            })),

        ]),
    };
};

/** The keyword page in MenuTrends. */
export const buildMenuTrendsKeywordUrl = (menuTrendsUrl: string, word: string) =>
    sanitizeUrl(`${menuTrendsUrl}${process.env.REACT_APP_MT_BYO_LINK}${encodeURIComponent(word)}`);
